import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { usePathname, navigate } from "../../lib/router";
import type { Payment, RecurringPlan } from "../../types";
import { SectionCard } from "../../components/SectionCard";
import { Button } from "../../components/ui/button";
import { PaymentsSection } from "../clientDetails/components/PaymentsSection";
import { centsToCurrency } from "../utils/money";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatWeekday, formatTime } from "../utils/schedule";
import { ChevronLeft } from "../../components/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";

function periodLabel(p: string) {
  const map: Record<string, string> = {
    monthly: "Mensal",
    quarterly: "Trimestral",
    semiannual: "Semestral",
    annual: "Anual",
  };
  return map[p] || p;
}

export default function PlanDetailsPage() {
  const { lang } = useLanguage();
  const path = usePathname();
  const planId = useMemo(() => {
    const parts = path.split("/");
    const idStr = parts[parts.length - 1];
    const n = Number(idStr);
    return Number.isFinite(n) ? n : null;
  }, [path]);

  const [plan, setPlan] = useState<(RecurringPlan & { client_name?: string; client_uid?: string; employee_name?: string }) | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCancelPlanOpen, setCancelPlanOpen] = useState(false);

  const load = async () => {
    if (!planId) return;
    setLoading(true);
    const res = await api.getRecurringPlan(planId);
    if (res.ok) {
      const data = res.data as any;
      const { payments: pays, schedules, ...p } = data;
      setPlan({ ...p, schedules });
      setPayments(pays || []);
    }
    setLoading(false);
  };

  const handleCancelPlan = async () => {
    if (!planId) return;
    const res = await api.deleteRecurringPlan(planId);
    if (res.ok) {
      if (plan?.client_uid) {
        navigate(`/app/clients/${plan.client_uid}`);
      } else {
        navigate('/app/clients');
      }
    } else {
      console.error("Failed to cancel plan", res);
      alert("Falha ao cancelar o plano. Tente novamente.");
    }
  };

  useEffect(() => { load(); }, [planId]);

  if (!planId) return <div className="p-4">Plano inválido</div>;

  return (
    <div className="grid grid-cols-1 gap-6">
      <SectionCard
        header={(
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="h-9 w-9 p-0"
              aria-label="Voltar"
              onClick={() => {
                if (plan?.client_uid) navigate(`/app/clients/${plan.client_uid}`);
                else navigate('/app/clients');
              }}
              icon={<ChevronLeft className="h-5 w-5" />}
            />
              <div className="font-semibold flex items-center gap-2">
              <span>{plan?.name || 'Plano'}</span>
              {plan && !plan.active && <Badge variant="danger">Inativo</Badge>}
            </div>
            </div>
          )}
          action={
            <div className="flex items-center gap-2">
              <Button onClick={() => { /* TODO: open edit modal */ }}>Editar plano</Button>
              <Button variant="destructive" onClick={() => setCancelPlanOpen(true)}>Cancelar Plano</Button>
            </div>
          }
        >
          {loading && <div className="text-sm text-neutral-600">Carregando...</div>}
        {!loading && plan && (
          <div className="space-y-2 text-sm">
            <div><span className="text-neutral-500">Cliente:</span> {plan.client_name || plan.client_id}</div>
            <div><span className="text-neutral-500">Profissional:</span> {plan.employee_name || plan.employee_id}</div>
            <div><span className="text-neutral-500">Valor:</span> {centsToCurrency(plan.price_cents, lang)}</div>
            <div><span className="text-neutral-500">Cobrança:</span> {periodLabel(plan.billing_period)} (dia {plan.billing_day})</div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(plan.schedules || []).map((s: any) => (
                <span key={s.id} className="inline-flex items-center px-2 py-1 border rounded-md bg-neutral-50">
                  {formatWeekday(s.weekday, lang)} {formatTime(s.start_time, lang)}–{formatTime(s.end_time, lang)}
                </span>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      <PaymentsSection
        payments={payments}
        onUpdated={(p) => setPayments(prev => {
          if ((p as any).total_amount_cents === -1) return prev.filter(x => x.id !== p.id);
          return prev.map(x => x.id === p.id ? p : x);
        })}
        onRefreshed={(list) => setPayments(list)}
      />

      <AlertDialog open={isCancelPlanOpen} onOpenChange={setCancelPlanOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Plano</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar este plano? Todas as consultas futuras
              agendadas serão removidas. Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelPlan}>
              Sim, cancelar plano
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
